import { HEIGHT_STATUS_BAR, componentName } from '@common/constants/shared';
import { appInfoSelector } from '@common/redux/selectors/app';
import { dimensionSelector, pagesSelector } from '@common/redux/selectors/page';
import { useLocation } from '@common/routes/hooks';
import BindingComponent from '@common/screens/RenderScreen/BindingComponent';
import ObjectRenderV4 from '@common/screens/RenderScreen/ObjectRender/ObjectRenderV4';
import { ElementType, ObjectRenderProps } from '@common/types/element';
import { mapV3StyleToV4Style, updateElementColor } from '@common/utils/color';
import { AppVersion } from 'click-types';
import { isNil, sortBy } from 'lodash';
import qs, { ParsedQuery } from 'query-string';
import React, { FC, Fragment, useMemo } from 'react';
import { Platform, View, StatusBar } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import { mapping, notScaleComponent } from './func';

const ObjectRender = ({
  arrComp,
  isScreen,
  layout,
  isShowStatusBar,
  screen,
}: ObjectRenderProps) => {
  const dimension = useSelector(dimensionSelector);
  const appInfo = useSelector(appInfoSelector);

  const insets = useSafeAreaInsets();

  const { search } = useLocation();

  const query: ParsedQuery<any> = qs.parse(search);

  const pages = useSelector(pagesSelector);

  const currentPageInfo = useMemo(
    () => (screen ? screen : pages[query?.target]),
    [pages, query, screen]
  );

  const originX = currentPageInfo ? dimension.width / currentPageInfo.width : 0;
  const originY = currentPageInfo
    ? dimension.height / currentPageInfo.height
    : 0;

  const isWeb = useMemo(() => appInfo?.platform === 'web', [appInfo]);

  // add z-Index based on canvas position
  arrComp.forEach((obj, i) => {
    obj.zIndex = obj.zIndex || i + 1;
  });

  let offsetLeft = layout?.offsetLeft || 0;
  let offsetTop = layout?.offsetTop || 0;
  let sortArrComp: ElementType[] = sortBy(arrComp, ['y', 'x']);

  if (!sortArrComp.length) return <Fragment />;

  let res = [];

  for (let i = 0; i < sortArrComp.length; i++) {
    let obj = JSON.parse(JSON.stringify(sortArrComp[i])),
      prevObj = sortArrComp[i - 1];

    // get function on sortArrComp[i]
    obj = {
      ...obj,
      ...(screen?.screenUuid && { screenUuid: screen?.screenUuid }),
      setEnableScrollViewScroll: sortArrComp[i].setEnableScrollViewScroll,
    };
    const isGroupChild = !isNil(layout?.parentWidth) && !isScreen;
    const originalWidth = obj.width;
    obj = mapV3StyleToV4Style(obj);
    if (isGroupChild) {
      obj.width = originalWidth;
      if (obj.style && obj.componentName != componentName.ActionButton) {
        obj.style.width = undefined;
      }
    }

    if (
      Array.isArray(obj.breakpointManifest) &&
      obj.breakpointManifest.length > 0
    ) {
      let breakpointKey: string;
      if (dimension.width < 768) {
        breakpointKey = 'MOBILE';
      } else if (dimension.width < 1200) {
        breakpointKey = 'TABLET';
      } else {
        breakpointKey = 'DESKTOP';
      }
      const manifest = obj.breakpointManifest.find(
        (m: any) => m.key === breakpointKey
      );
      if (manifest?.width != null) obj.width = manifest.width;
      if (manifest?.height != null) obj.height = manifest.height;
    }

    const statusBarHeight = isWeb ? 0 : HEIGHT_STATUS_BAR * originX;

    // On iOS, StatusBar.currentHeight is undefined, so use safe area insets
    // On Android, use StatusBar.currentHeight
    const currentStatusBarHeight =
      Platform.OS === 'ios'
        ? insets.top
        : Platform.OS === 'android' && StatusBar.currentHeight
        ? StatusBar.currentHeight
        : 0;

    if (i == 0) {
      const marginTopPocth =
        Platform.OS !== 'ios' && obj.y < statusBarHeight
          ? statusBarHeight - obj?.y || 0
          : 0;

      const safeAreaOffset =
        appInfo?.version === AppVersion.V4
          ? currentStatusBarHeight
          : obj.y === 0
          ? currentStatusBarHeight
          : 0;

      obj.marginTop = !isScreen
        ? obj.y - offsetTop
        : obj.y +
          (isShowStatusBar ? -statusBarHeight + marginTopPocth : 0) +
          safeAreaOffset;
    } else {
      const prevHeightObject =
        prevObj.type === 'label'
          ? prevObj.height + 2 * prevObj.borderWidth + prevObj.borderRadius
          : prevObj.height;
      let marginTop = obj.y - prevObj.y - prevHeightObject;
      obj.marginTop = marginTop;
    }

    // set marginLeft and width component and merge to originX
    if (obj?.screenWidth != dimension.width) {
      obj.resizeMode = obj?.screenWidth ? true : false;
      obj.screenWidth = dimension.width;
      obj.originX = originX;
      obj.originY = originY;

      let scaleWidth = obj.width * originX;

      if (isWeb) {
        obj.isWeb = isWeb;
        if (obj.width / (currentPageInfo?.width || 1) >= 0.9) {
          obj.width = obj.width * originX;
          scaleWidth = obj.width;
        } else if (obj.width / dimension.width >= 0.9) {
          obj.width = dimension.width * 0.9;
          scaleWidth = obj.width;
        }
      } else {
        if (notScaleComponent.includes(obj?.type)) {
          obj.width = obj.width * originX;
          // obj.height = obj.height * originX;
        } else {
          obj.width = obj.width * originX;
        }
      }

      let webOffset = 0;

      if (isWeb) {
        webOffset = (scaleWidth - obj.width) / 2;
      } else {
        webOffset = notScaleComponent.includes(obj?.type)
          ? (scaleWidth - obj.width) / 2
          : 0;
      }

      if (isNil(layout?.offsetLeft)) {
        obj.marginLeft =
          obj.x === 0 ? 0 : (obj.x - offsetLeft) * originX + webOffset;
      } else {
        obj.marginLeft = (obj.x - offsetLeft) * originX;

        const objTopRight = obj.x + obj.width;
        const parentTopRight =
          (layout?.offsetLeft || 0) + (layout?.parentWidth || 0);
        if (objTopRight >= parentTopRight) {
          obj.marginLeft = obj.marginLeft - (objTopRight - parentTopRight);

          if (obj.marginLeft < 0) {
            obj.marginLeft = (obj.x - offsetLeft) * originX;
          }
        }
      }
    } else {
      obj.resizeMode = false;
      // Special case: Handle Group children that need scaling
      // When a child is inside a Group with originX from parent, but screenWidth matches dimension
      // The normal scaling block above is skipped, so we need to scale here
      if (isGroupChild && obj?.originX) {
        obj.width = obj.width * obj.originX;
        obj.marginLeft = (obj.x - offsetLeft) * obj.originX;
        obj.resizeMode = true;
      }
    }
    let ObjectClass = mapping[obj.type] as FC;
    if (!ObjectClass) {
      console.log(`I don't know how to render ${obj.name}`);
      res.push(<React.Fragment key={i}></React.Fragment>);
      continue;
    } else {
      // update colors
      if (appInfo) {
        obj = updateElementColor(obj, appInfo);
      }
      if (obj?.fixPosition == 'top' && isScreen) {
        // case of top fixed element
        delete obj.marginTop;
        const left = obj.marginLeft;
        const zIndex = obj.zIndex;
        delete obj.marginLeft;
        //delete obj.zIndex;
        const shouldAddSafeArea =
          appInfo?.version === AppVersion.V4 || obj.y === 0;
        const topOffset =
          obj.y * originY + (shouldAddSafeArea ? currentStatusBarHeight : 0);

        res.push(
          <View
            style={{
              top: topOffset,
              left,
              zIndex,
              position: 'absolute',
            }}
            key={i}
          >
            <BindingComponent key={i} obj={obj} ObjectClass={ObjectClass} />
          </View>
        );
      } else if (obj?.fixPosition == 'bottom' && isScreen) {
        // case of bottom fixed element
        delete obj.marginTop;
        const left = obj.marginLeft;
        const zIndex = obj.zIndex;
        delete obj.marginLeft;
        // delete obj.zIndex;
        res.push(
          <View
            style={{
              bottom: currentPageInfo
                ? (currentPageInfo.height - obj.height - obj.y) * originY
                : 0,
              left,
              zIndex,
              position: 'absolute',
            }}
            key={i}
          >
            <BindingComponent key={i} obj={obj} ObjectClass={ObjectClass} />
          </View>
        );
      } else {
        // case of position relative element
        res.push(
          <BindingComponent key={i} obj={obj} ObjectClass={ObjectClass} />
        );
      }
    }
  }

  return <React.Fragment>{res}</React.Fragment>;
};

// Wrapper for render list of objects, if version is v4, use ObjectRenderV4, otherwise use ObjectRender to avoid update the legacy apps.
export default function ObjectRenderWrapper(props: ObjectRenderProps) {
  const appInfo = useSelector(appInfoSelector);
  if (appInfo?.version === AppVersion.V4) {
    return <ObjectRenderV4 {...props} />;
  }
  return <ObjectRender {...props} />;
}
